import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import IServicioIndividual from "../../interfaces/IServicioIndividual";
import IModalidadPago from "../../interfaces/IModalidadPago";
import Api from "../../utils/api";
import ModalMediosPago from "./ModalMediosPago";
import usePagoOnline from "../../hooks/usePagoOnline.hook";
import Button from "../Button";
import BarraPagoFlotante from "../BarraPagoFlotante";
import DownloadFile from "../../utils/downloadFile";

import "./ServiciosPreViaje.css";

interface IProps {
  servicios: IServicioIndividual[];
}

interface IPropsRow {
  servicio: IServicioIndividual;
  i: number;
  togglePagoOnline: Function;
  checked: boolean;
}

function Row({ servicio, i, togglePagoOnline, checked }: IPropsRow) {
  const [print, setPrint] = useState<boolean>(false);
  const { id = "-1" } = useParams();

  const handleClickPrint = () => {
    setPrint(true);
    Api("birt")
      .post(
        {
          param: {
            prm_co_pasajero: id,
            prm_nu_talon: servicio.NU_TALON,
          },
          reporte: "SV_cupon_SI.rptdesign",
        },
        "text/plain"
      )
      .then((res: string) => {
        DownloadFile(res, `${id} - Talon ${servicio.NU_TALON}`);
      })
      .finally(() => {
        setPrint(false);
      });
  };

  return (
    <div className="grid-row">
      <span title="Servicio individual" className="th-title">
        {i}
      </span>
      <span title="Fecha vencimiento">{servicio.F_VENCIMIENTO}</span>
      <span title="Importe">
        {servicio.MONEDA}
        {servicio.D_IMPORTE}
      </span>
      <span title="Pago online">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => togglePagoOnline(servicio, e.target.checked)}
        />
      </span>
      <span title="Tipo">{servicio.D_TIPO}</span>
      <span title="Lista de servicios">{servicio.D_LISTA_SERVICIOS}</span>
      <span title="Imprimir">
        {servicio.BOT_IMPRIMIR ? (
          <Button
            title="Imprimir"
            onLoad={print}
            onPress={() => handleClickPrint()}
          />
        ) : (
          <></>
        )}
      </span>
    </div>
  );
}

export default function ServiciosPreViaje({ servicios = [] }: IProps) {
  const [msgError, setMgError] = useState<string>("");

  const [pagoOnline, setPagoOnline] = useState<Array<IServicioIndividual>>([]);
  const [totalPagoOnline, setTotalPagoOnline] = useState<number>(0);

  const [showModalPago, setShowModalPago] = useState(false);

  const pago = usePagoOnline();
  const { id = "-1" } = useParams();

  const togglePagoOnline = (servicio: IServicioIndividual, state: boolean) => {
    if (state) {
      setPagoOnline([...pagoOnline, servicio]);
      return;
    }
    setPagoOnline(
      pagoOnline.filter(
        (e: IServicioIndividual) => e.NU_TALON !== servicio.NU_TALON
      )
    );
  };

  useEffect(() => {
    setMgError("");
    const total = pagoOnline.reduce(
      (acc: number, e: IServicioIndividual) => acc + e.IMP_PAGAR,
      0
    );
    setTotalPagoOnline(total);
  }, [pagoOnline]);

  // Abre el modal y pide las modalidades de pago disponibles.
  const handleClickPagoOnline = () => {
    if (!pagoOnline.length) {
      setMgError("Por favor, selecciona alguna cuota");
      return;
    }
    const ptalones = pagoOnline.map((e) => e.NU_TALON).join(",");
    setShowModalPago(true);
    pago.fetchModalidades({
      prm_co_pasajero: id,
      prm_talones: ptalones,
      prm_importe_ctl: totalPagoOnline,
      urls: {
        url_success: window.location.origin + "/pagoResultado",
        url_cancel: window.location.origin + window.location.pathname,
        url_redirect: window.location.origin + "/pagoResultado",
      },
    });
  };

  // Elegida la opción, inicia el pago y redirige a la recaudadora.
  const handleSelectModalidad = async (modalidad: IModalidadPago) => {
    const url = await pago.iniciarPago(modalidad);
    if (url) {
      window.location.href = url;
    }
  };

  const handleCerrar = () => {
    setShowModalPago(false);
    pago.reset();
  };

  return (
    <div className="servicios-previaje">
      <h4>Servicios Pre-Viaje:</h4>
      <div className="d-grid grid-container">
        <div className="th-platform">
          <span>Fecha vencimiento</span>
          <span>Importe</span>
          <span>Pago online</span>
          <span>Tipo</span>
          <span>Lista de servicios</span>
          <span></span>
        </div>
        <div className="td-platform">
          {servicios.map((servicio, i) => (
            <Row
              key={servicio.NU_TALON}
              servicio={servicio}
              i={i + 1}
              checked={pagoOnline.some(
                (e) => e.NU_TALON === servicio.NU_TALON
              )}
              togglePagoOnline={(a: IServicioIndividual, b: boolean) =>
                togglePagoOnline(a, b)
              }
            />
          ))}
        </div>
      </div>
      <h5 className="mt-2 text-danger text-center">{msgError}</h5>
      <BarraPagoFlotante
        importe={totalPagoOnline}
        onPagar={handleClickPagoOnline}
        onCancelar={() => setPagoOnline([])}
      />
      <ModalMediosPago
        show={showModalPago}
        onClose={handleCerrar}
        modalidades={pago.modalidades}
        loadingModalidades={pago.loadingModalidades}
        payingToken={pago.payingToken}
        error={pago.error}
        onSelectModalidad={handleSelectModalidad}
      />
    </div>
  );
}
